import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  LineChart,
  BarChart,
  AreaChart,
  ScatterChart,
  PieChart,
  ComposedChart,
  Line,
  Bar,
  Area,
  Scatter,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartSpec } from '../types/chat.types';
import { BRAND_COLORS } from '../../../styles/theme';

const CHART_PALETTE = [
  BRAND_COLORS.forestGreen.main,
  BRAND_COLORS.yellow.main,
  '#2196F3',
  '#FF5722',
  '#9C27B0',
  '#00BCD4',
  '#FF9800',
  '#4CAF50',
];

interface ChartRendererProps {
  spec: ChartSpec;
  height?: number;
}

interface TooltipPayloadItem {
  name?: string;
  value?: number | string;
  color?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  unit?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: entry.color }}>
          {entry.name}:{' '}
          {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          {unit ? ` ${unit}` : ''}
        </Typography>
      ))}
    </Box>
  );
};

const ChartRenderer: React.FC<ChartRendererProps> = ({ spec, height = 300 }) => {
  const { chart_type, title, x_axis, y_axis, series, data } = spec;

  if (!data?.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available for chart
        </Typography>
      </Box>
    );
  }

  const getColor = (index: number, seriesConfig?: { color?: string }) =>
    seriesConfig?.color || CHART_PALETTE[index % CHART_PALETTE.length];

  const commonProps = {
    data,
    margin: { top: 5, right: 20, bottom: 5, left: 10 },
  };

  const renderAxes = () => (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis
        dataKey={x_axis.key}
        tick={{ fontSize: 11 }}
        label={
          x_axis.label
            ? { value: x_axis.label, position: 'insideBottom', offset: -5, fontSize: 11 }
            : undefined
        }
      />
      <YAxis
        tick={{ fontSize: 11 }}
        label={
          y_axis?.label
            ? {
                value: `${y_axis.label}${y_axis.unit ? ` (${y_axis.unit})` : ''}`,
                angle: -90,
                position: 'insideLeft',
                fontSize: 11,
              }
            : undefined
        }
      />
      <Tooltip content={<CustomTooltip unit={y_axis?.unit} />} />
      {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
    </>
  );

  const renderChart = () => {
    switch (chart_type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            {renderAxes()}
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label || s.key}
                stroke={getColor(i, s)}
                strokeWidth={2}
                dot={data.length <= 30}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            {renderAxes()}
            {series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.label || s.key} fill={getColor(i, s)} />
            ))}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            {renderAxes()}
            {series.map((s, i) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label || s.key}
                stroke={getColor(i, s)}
                fill={getColor(i, s)}
                fillOpacity={0.3}
              />
            ))}
          </AreaChart>
        );

      case 'scatter':
        return (
          <ScatterChart margin={commonProps.margin}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey={x_axis.key} name={x_axis.label || x_axis.key} tick={{ fontSize: 11 }} />
            <YAxis
              dataKey={series[0]?.key}
              name={y_axis?.label || series[0]?.key}
              tick={{ fontSize: 11 }}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Scatter
              name={series[0]?.label || series[0]?.key}
              data={data}
              fill={getColor(0, series[0])}
            />
          </ScatterChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={series[0]?.key || 'value'}
              nameKey={x_axis.key}
              cx="50%"
              cy="50%"
              outerRadius={Math.min(height * 0.35, 100)}
              label={({ name, value }: { name: string; value: number }) => `${name}: ${value}`}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        );

      case 'composed':
        return (
          <ComposedChart {...commonProps}>
            {renderAxes()}
            {series.map((s, i) => {
              const color = getColor(i, s);
              switch (s.type) {
                case 'bar':
                  return <Bar key={s.key} dataKey={s.key} name={s.label || s.key} fill={color} />;
                case 'area':
                  return (
                    <Area
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label || s.key}
                      stroke={color}
                      fill={color}
                      fillOpacity={0.3}
                    />
                  );
                default:
                  return (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      name={s.label || s.key}
                      stroke={color}
                      strokeWidth={2}
                    />
                  );
              }
            })}
          </ComposedChart>
        );

      default:
        return (
          <LineChart {...commonProps}>
            {renderAxes()}
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label || s.key}
                stroke={getColor(i, s)}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        );
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </Box>
  );
};

export default ChartRenderer;
